
import React, { useState } from 'react';
import { Shield, Crown, Trash2, MoreVertical, Info, Calendar, Clock, Pencil } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { EditServerDialog } from '@/components/EditServerDialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useAdminPrestige } from '@/hooks/useAdminPrestige';
import { useEditServer } from '@/hooks/useEditServer';
import { useAuth } from '@/hooks/useAuth';
import { PrestigeDaysDialog } from '@/components/PrestigeDaysDialog';

interface AdminPanelProps {
  server: {
    id: string;
    name: string;
    is_sponsored?: boolean;
    prestige_expires_at?: string;
    created_at?: string;
    updated_at?: string;
  };
  className?: string;
}

export const AdminPanel: React.FC<AdminPanelProps> = ({ server, className = "" }) => {
  const { user } = useAuth();
  const { hasPrivileges, activatePrestige, deactivatePrestige } = useAdminPrestige();
  const { deleteServer } = useEditServer();
  
  const [showPrestigeDaysDialog, setShowPrestigeDaysDialog] = useState(false);
  const [showDeactivateDialog, setShowDeactivateDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [fullServerData, setFullServerData] = useState<any>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Vérifier si l'utilisateur est fondateur ou admin
  const isFounderOrAdmin = hasPrivileges;
  
  if (!isFounderOrAdmin) return null;

  // Vérifier le statut Prestige plus précisément
  const isPrestige = server.is_sponsored && 
                    server.prestige_expires_at && 
                    new Date(server.prestige_expires_at) > new Date();

  //console.log('AdminPanel Debug:', {
  //  serverId: server.id,
  //  serverName: server.name,
  //  is_sponsored: server.is_sponsored,
  //  prestige_expires_at: server.prestige_expires_at,
  //  isPrestige,
  //  currentDate: new Date().toISOString()
  //});

  const handleActivatePrestige = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(false);
    setShowPrestigeDaysDialog(true);
  };

  const handleDeactivatePrestige = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(false);
    setShowDeactivateDialog(true);
  };

  const confirmActivatePrestige = (days: number) => {
    activatePrestige.mutate({ serverId: server.id, days });
    setShowPrestigeDaysDialog(false);
  };

  const confirmDeactivatePrestige = () => {
    deactivatePrestige.mutate(server.id);
    setShowDeactivateDialog(false);
  };

  const handleEditServer = async () => {
    setIsMenuOpen(false);
    setFullServerData((prev: any) => prev ?? {
      id: server.id,
      name: server.name,
      description: '',
      ip_address: '',
      version: '',
      website_url: '',
      discord_url: '',
      tags: [],
      logo_image_url: '',
      banner_image_url: '',
      description_image_url: '',
      custom_color: '',
      game_id: '',
      is_sponsored: !!server.is_sponsored,
      prestige_expires_at: server.prestige_expires_at || null,
    });
    setShowEditDialog(true);

    const { data } = await supabase
      .from('servers')
      .select('*')
      .eq('id', server.id)
      .single();

    if (data) {
      setFullServerData(data);
    }
  };

  const handleDeleteServer = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMenuOpen(false);
    setShowDeleteDialog(true);
  };

  const confirmDeleteServer = () => {
    deleteServer(server.id);
    setShowDeleteDialog(false);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Non disponible';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPrestigeStatus = () => {
    if (!server.is_sponsored || !server.prestige_expires_at) {
      return 'Aucun';
    }
    
    const expiresAt = new Date(server.prestige_expires_at);
    const now = new Date();
    
    if (expiresAt > now) {
      return `Actif jusqu'au ${formatDate(server.prestige_expires_at)}`;
    } else {
      return `Expiré le ${formatDate(server.prestige_expires_at)}`;
    }
  };

  return (
    <>
      <DropdownMenu modal={false} open={isMenuOpen} onOpenChange={setIsMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={`admin-panel-trigger p-2 bg-black/60 hover:bg-black/80 backdrop-blur-sm rounded-full border border-red-500/30 transition-all duration-200 ${className}`}
            title="Actions Administrateur"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Shield className="w-4 h-4 text-red-400" />
            <MoreVertical className="w-3 h-3 text-red-400" />
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent 
          className="w-64 bg-slate-800 border-slate-700"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenuLabel className="text-red-400 font-mono">
            🛡️ Actions Privilégiées
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-slate-700" />
          
          {/* Sous-menu Informations */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-blue-400 hover:bg-slate-700">
              <Info className="w-4 h-4 mr-2" />
              Informations Serveur
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-80 bg-slate-800 border-slate-700 p-3">
              <div className="space-y-3 text-sm">
                <div className="border-b border-slate-600 pb-2">
                  <h4 className="text-blue-400 font-semibold mb-2">📊 Informations Générales</h4>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-green-400" />
                    <span className="text-slate-300">
                      <strong>Créé le :</strong> {formatDate(server.created_at)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-yellow-400" />
                    <span className="text-slate-300">
                      <strong>Modifié le :</strong> {formatDate(server.updated_at)}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Crown className="w-4 h-4 text-purple-400" />
                    <span className="text-slate-300">
                      <strong>Statut Prestige :</strong> {getPrestigeStatus()}
                    </span>
                  </div>
                </div>

                {isPrestige && (
                  <div className="mt-3 p-2 bg-purple-900/30 rounded border border-purple-500/30">
                    <p className="text-xs text-purple-300">
                      🌟 Ce serveur bénéficie actuellement du statut Prestige
                    </p>
                  </div>
                )}
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuSub>

          <DropdownMenuSeparator className="bg-slate-700" />
          
          {/* Action Éditer */}
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault();
              void handleEditServer();
            }}
            className="text-blue-400 hover:bg-slate-700 cursor-pointer"
          >
            <Pencil className="w-4 h-4 mr-2" />
            Éditer le serveur
          </DropdownMenuItem>

          {/* Action Prestige */}
          <DropdownMenuItem
            onClick={isPrestige ? handleDeactivatePrestige : handleActivatePrestige}
            className="text-yellow-400 hover:bg-slate-700 cursor-pointer"
          >
            <Crown className="w-4 h-4 mr-2" />
            {isPrestige ? 'Désactiver Prestige' : 'Activer Prestige'}
          </DropdownMenuItem>
          
          {/* Action Suppression */}
          <DropdownMenuItem
            onClick={handleDeleteServer}
            className="text-red-400 hover:bg-slate-700 cursor-pointer"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Supprimer le serveur
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Dialog de sélection des jours pour activation Prestige */}
      <PrestigeDaysDialog
        isOpen={showPrestigeDaysDialog}
        onClose={() => setShowPrestigeDaysDialog(false)}
        onConfirm={confirmActivatePrestige}
        serverName={server.name}
        isLoading={activatePrestige.isPending}
      />

      {/* Dialog de confirmation Désactivation Prestige */}
      <AlertDialog open={showDeactivateDialog} onOpenChange={setShowDeactivateDialog}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              🛡️ Désactiver le Prestige
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Désactiver le Prestige pour "{server.name}" ? Cette action est immédiate et réservée aux fondateurs/administrateurs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white hover:bg-slate-600">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeactivatePrestige}
            >
              Désactiver Prestige
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog de confirmation Suppression */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-slate-800 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">
              🛡️ Suppression Définitive
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Supprimer définitivement le serveur "{server.name}" ? Cette action est irréversible et supprimera toutes les données associées (votes, commentaires, etc.).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-700 text-white hover:bg-slate-600">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction 
              className="bg-red-600 hover:bg-red-700"
              onClick={confirmDeleteServer}
            >
              Supprimer Définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Dialog Édition serveur (admin) */}
      {fullServerData && (
        <EditServerDialog
          server={fullServerData}
          isOpen={showEditDialog}
          onClose={() => {
            setShowEditDialog(false);
            setFullServerData(null);
          }}
        />
      )}
    </>
  );
};
